import { Controller, DefaultValues, FormProvider, useForm } from "react-hook-form"
import { CustomerType } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { ProductionPointFormValues, ProductionProduct, useProductionPointFormSchema } from "./productionPointForm.utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { NavigationButtons } from "../components/NavigationButtons"
import { Subheader } from "../components/Subheader"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { theme } from "../../../../../styles/stitches.config"
import { FormItem, FormLabel } from "../components/Form"
import { getFormError, getFormSuccess } from "../../../../../utils/form"
import { FormSelect } from "../components/Select"
import { invoicingMethodOptions } from "@nano-portal/customers"
import { FormInput, InputDisclaimer } from "../components/Input"
import { FileInput, FileInputDisclaimer } from "../components/FileInput"
import { PowerStationTypeSection } from "./PowerStationTypeSection"
import { ProductionPointEanField } from "./ProductionPointEanField"

type Props = {
  customerType: CustomerType
  selectedProduct: ProductionProduct
  defaultValues?: DefaultValues<ProductionPointFormValues>
  onReturn?: () => void
  onSubmit?: (data: ProductionPointFormValues) => void
}

export function ProductionPointForm(props: Props) {
  const schema = useProductionPointFormSchema(props.selectedProduct)
  const form = useForm<ProductionPointFormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      invoicingMethod: "SUPPLIER_ISSUED_INVOICE",
      powerStationInfo: props.defaultValues?.powerStationInfo ?? {
        powerStationType: "EXISTING",
        contractInfo: {
          terminationOption: "TERMINATE",
          currentSupplier: "",
          currentContract: {
            contractType: "VARIABLE_TERM",
            noticePeriod: "ONE_MONTH",
          },
        },
      },
      ...props.defaultValues,
    },
  })
  const connectionContractWatch = form.watch("connectionContract")
  const contractTerminationWatch = form.watch("powerStationInfo.contractInfo.contractTermination")

  function handleSubmit(formValues: ProductionPointFormValues) {
    // overriding the file objects because the zod schema serialization throws away the file object itself
    formValues.connectionContract = connectionContractWatch
    if (
      formValues.powerStationInfo.powerStationType === "EXISTING" &&
      formValues.powerStationInfo.contractInfo?.terminationOption === "DO_NOT_TERMINATE" &&
      formValues.powerStationInfo.contractInfo?.contractTermination
    ) {
      formValues.powerStationInfo.contractInfo.contractTermination = contractTerminationWatch
    }

    return props.onSubmit?.(formValues)
  }

  return (
    <FormProvider {...form}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={form.handleSubmit(handleSubmit, (e) => console.log(e))}>
        <Subheader asSectionHeader>Informace o výrobním místě</Subheader>

        <Spacer size={theme.space.s6} />

        <ProductionPointEanField selectedProduct={props.selectedProduct} />

        <Spacer size={theme.space.s9} />

        <Controller
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Název výrobny</FormLabel>
              <FormInput
                id="name"
                placeholder="např. Domácí FVE"
                {...field}
                error={getFormError({ form, name: "name" })}
                success={getFormSuccess({ form, name: "name" })}
              />
            </FormItem>
          )}
        />

        <Spacer size={theme.space.s9} />

        <FormItem>
          <FormLabel>Výkon v kWp</FormLabel>
          <FormInput
            id="powerKwp"
            placeholder="max. 50 kWp"
            type="number"
            step={0.01}
            {...form.register("powerKwp", { valueAsNumber: true })}
            error={getFormError({ form, name: "powerKwp" })}
            success={getFormSuccess({ form, name: "powerKwp" })}
          />
        </FormItem>

        <Spacer size={theme.space.s3} />
        <InputDisclaimer
          label={
            <p>
              Lze sjednat pouze smlouvu na FVE v režimu mikrozdroje, tedy max. 50 kWp výkonu. Jste-li licencovaným
              výrobcem, kontaktujte nás na{" "}
              <a
                href="mailto:info@deltagreen.cz"
                style={{ color: "unset", cursor: "pointer", textDecoration: "underline" }}
              >
                info@deltagreen.cz
              </a>
              .
            </p>
          }
        />

        <Spacer size={theme.space.s9} />

        <PowerStationTypeSection selectedProduct={props.selectedProduct} />

        {props.customerType === "business" && (
          <>
            <Spacer size={theme.space.s9} />

            <Controller
              control={form.control}
              name="invoicingMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Způsob fakturace za prodej elektřiny</FormLabel>
                  <FormSelect
                    id="power-station-type"
                    options={invoicingMethodOptions}
                    {...field}
                    error={getFormError({ form, name: "invoicingMethod" })}
                    success={getFormSuccess({ form, name: "invoicingMethod" })}
                  />
                </FormItem>
              )}
            />

            <Spacer size={theme.space.s3} />

            <InputDisclaimer
              label={
                <p>
                  Doporučujeme fakturaci nechat na nás - postaráme se o ni zcela za vás a celý proces vám tím usnadníme.
                </p>
              }
            />
          </>
        )}

        <Spacer size={theme.space.s9} />

        <Controller
          control={form.control}
          name="connectionContract"
          render={({ field: { value, ref, onChange, onBlur } }) => {
            const fileError = getFormError({ form, name: "connectionContract" })
            const typeError = getFormError({ form, name: "connectionContract.type" })
            const sizeError = getFormError({ form, name: "connectionContract.size" })

            const errorMessage = fileError ?? typeError ?? sizeError

            return (
              <FormItem>
                <FormLabel>Protokol o prvním paralelním připojení (PPP) / Smlouva o připojení (SoP)</FormLabel>
                <FileInput
                  id="power-station-ppp-sop"
                  uploadedFileName={errorMessage ? undefined : value?.name}
                  error={errorMessage}
                  ref={ref}
                  onClear={() => form.resetField("connectionContract", { defaultValue: "" })}
                  onChange={(e) => {
                    onChange(e.target.files?.[0])
                    // we are not interacting with the input element itself, but with a custom label,
                    // therefore need to trigger onBlur manually in order to trigger onBlur validation
                    onBlur()
                  }}
                  onBlur={onBlur}
                />
              </FormItem>
            )
          }}
        />

        <Spacer size={theme.space.s3} />

        <FileInputDisclaimer />

        <Spacer size={theme.space.s12} />

        <NavigationButtons
          prevText={props.selectedProduct === "productionAndConsumption" ? "Odběrné místo" : "Osobní údaje"}
          nextText="Platební údaje"
          onReturn={props.onReturn}
          isSubmitting={form.formState.isSubmitting}
        />
      </form>
    </FormProvider>
  )
}
